import React from 'react';
import { MsalProvider, useMsal, useIsAuthenticated } from '@azure/msal-react';
import { PublicClientApplication, InteractionStatus } from '@azure/msal-browser';
import { msalConfig, loginRequest } from '@/services/authConfig';
import { Loader2, ShieldCheck } from 'lucide-react';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const msalInstance = new PublicClientApplication(msalConfig);

/** Top-level MSAL provider — wraps the entire app */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  return <MsalProvider instance={msalInstance}>{children}</MsalProvider>;
}

/** Gate component — shows login screen or children based on auth state */
export function AuthGate({ children }: { children: React.ReactNode }) {
  const { instance, inProgress } = useMsal();
  const isAuthenticated = useIsAuthenticated();

  if (inProgress === InteractionStatus.Login || inProgress === InteractionStatus.HandleRedirect) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-wire3-navy">
        <Loader2 className="h-10 w-10 animate-spin text-wire3-green" />
      </div>
    );
  }

  if (!isAuthenticated && import.meta.env.VITE_MOCK_AUTH !== "true") {
    return <LoginScreen onLogin={() => instance.loginRedirect(loginRequest)} />;
  }

  return <>{children}</>;
}

function LoginScreen({ onLogin }: { onLogin: () => void }) {
  return (
    <div className="flex min-h-screen flex-col items-center justify-center bg-wire3-navy px-6">
      {/* Logo */}
      <div className="mb-8 flex flex-col items-center">
        <div className="mb-4 flex h-20 w-20 items-center justify-center rounded-2xl bg-wire3-green/10">
          <ShieldCheck className="h-10 w-10 text-wire3-green" />
        </div>
        <h1 className="text-3xl font-bold text-white">Wire3</h1>
        <p className="mt-1 text-lg text-gray-400">Vehicle Inspection</p>
      </div>

      {/* Sign in button */}
      <button
        onClick={onLogin}
        className="flex w-full max-w-xs items-center justify-center gap-3 rounded-xl bg-white px-6 py-4 font-semibold text-wire3-navy shadow-lg transition-transform active:scale-[0.97]"
      >
        <svg className="h-5 w-5" viewBox="0 0 21 21" xmlns="http://www.w3.org/2000/svg">
          <rect x="1" y="1" width="9" height="9" fill="#f25022" />
          <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
          <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
          <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
        </svg>
        Sign in with Microsoft
      </button>

      <p className="mt-6 text-center text-sm text-gray-500">
        Use your Wire3 company account to sign in.
      </p>
    </div>
  );
}

/** Hook to get the current user's info from MSAL */
export function useCurrentUser() {
  const { accounts } = useMsal();
  const account = accounts[0];

  // Return mock user in dev/mock auth mode
  if (import.meta.env.VITE_MOCK_AUTH === 'true' || !account) {
    if (import.meta.env.DEV) {
      return {
        id: 'dev-user-001',
        name: 'Dev User',
        email: 'dev@wire3.com',
        role: 'admin' as const,
        roles: ['p:vi.admin', 'p:vi.supervisor', 'p:vi.inspector'],
      };
    }
    return null;
  }

  const roles: string[] = (account.idTokenClaims as any)?.roles ?? [];
  const role = roles.includes('Admin') ? 'admin'
    : roles.includes('Supervisor') ? 'supervisor'
    : 'inspector';

  return {
    id: account.localAccountId,
    name: account.name || account.username,
    email: account.username,
    role: role as 'inspector' | 'supervisor' | 'admin',
    roles,
  };
}

export function AuthRedirectHandler() {
  const { instance, inProgress } = useMsal();
  const navigate = useNavigate();

  useEffect(() => {
    if (inProgress === InteractionStatus.None) {
      instance.handleRedirectPromise()
        .then((result) => {
          if (result) {
            // Token was successfully captured, go to home page
            navigate('/inspect', { replace: true });
          } else {
            // No token in the URL, send back to login
            navigate('/', { replace: true });
          }
        })
        .catch((error) => {
          console.error('Redirect handling failed:', error);
          navigate('/', { replace: true });
        });
    }
  }, [inProgress]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-wire3-navy">
      <Loader2 className="h-10 w-10 animate-spin text-wire3-green" />
    </div>
  );
}
